import { useMemo } from 'react'

import {
  InvestmentAccountType,
  useAddSecurityToPortfolioMutation,
  useCryptoEtfQuery,
  useRemoveSecurityFromPortfolioMutation,
} from 'generated/types'

export const CRYPTO_GRADIENT = ['#6944FF', '#031125']
export const CRYPTO_SYMBOL = 'BITO'

export const useCrypto = () => {
  const { data: cryptoData, loading: isCryptoDataLoading } = useCryptoEtfQuery()

  const [
    addCryptoToPortfolio,
    { data: addCryptoToPortfolioData, loading: isAddCryptoMutationSubmitting },
  ] = useAddSecurityToPortfolioMutation()

  const [
    removeCryptoFromPortfolio,
    {
      data: removeCryptoFromPortfolioData,
      loading: isRemoveCryptoMutationSubmitting,
    },
  ] = useRemoveSecurityFromPortfolioMutation()

  const cryptoEtf =
    cryptoData?.cryptoEtf?.symbol === CRYPTO_SYMBOL
      ? cryptoData.cryptoEtf
      : null

  const { investmentAccount, currentPortfolio } = useMemo(() => {
    const investmentAccount = cryptoData?.investmentAccountsByUserId.find(
      (account) =>
        account.investmentAccountType ===
          InvestmentAccountType.BasicInvestment && !account.closedAt,
    )
    let currentPortfolio =
      investmentAccount?.portfolio ||
      addCryptoToPortfolioData?.addSecurityToPortfolio

    if (
      addCryptoToPortfolioData?.addSecurityToPortfolio.__typename ===
      'Portfolio'
    ) {
      currentPortfolio = addCryptoToPortfolioData.addSecurityToPortfolio
    }

    if (
      removeCryptoFromPortfolioData?.removeSecurityFromPortfolio.__typename ===
      'Portfolio'
    ) {
      currentPortfolio =
        removeCryptoFromPortfolioData.removeSecurityFromPortfolio
    }

    return {
      investmentAccount,
      currentPortfolio,
    }
  }, [cryptoData, addCryptoToPortfolioData, removeCryptoFromPortfolioData])

  const { eligibleForCrypto, currentPortfolioIncludesCrypto } = useMemo(() => {
    const currentPortfolioIncludesCrypto =
      investmentAccount?.portfolio?.portfolioSecurityAllocations?.some(
        (etf) => etf.investmentCategories.crypto > 0,
      ) ?? false

    return {
      eligibleForCrypto: !!(cryptoEtf && !currentPortfolioIncludesCrypto),
      currentPortfolioIncludesCrypto,
    }
  }, [cryptoData, investmentAccount])

  // @note: If the current portfolio includes crypto but we still
  // haven't purchased a share for the user, their portfolio
  // is still in an "unbalanced" state. This logic drives a
  // card where we explain this to the user.
  const isPortfolioBeingRebalanced =
    currentPortfolioIncludesCrypto &&
    !!((investmentAccount?.totalCrypto?.value ?? 0) <= 0)

  return {
    cryptoEtf,
    isCryptoDataLoading,
    investmentAccount,
    eligibleForCrypto,
    currentPortfolioIncludesCrypto,
    addCryptoToPortfolio,
    removeCryptoFromPortfolio,
    currentPortfolio,
    isAddCryptoMutationSubmitting,
    isRemoveCryptoMutationSubmitting,
    cryptoEtfSymbol: cryptoEtf?.symbol ?? 'BITO',
    isPortfolioBeingRebalanced,
  }
}
